import type {
  ResourceRecordPayload,
  SdkResource,
  SdkResourceType,
  SdkResourceRecord,
  SdkUpdateRecordHandle,
  SdkUseUpdateRecordOptions
} from '@Pages/pageRuntime/sdk/types';

import { useRecordMutation } from './useRecordMutation';

type UpdateVariables = {
  recordId: string;
  payload: ResourceRecordPayload;
};

/**
 * Reactive wrapper around `resource.update` for pages that write records without a
 * form hook — an inline status toggle, a drag-and-drop kanban column, a quick edit.
 * The request runs through the resource handle, so the page's cached record data is
 * refreshed on success exactly as it is for a direct `resource.update` call.
 *
 * Exposed via `Sdk.hooks.useUpdateRecord(resource, options)`.
 */
export function useUpdateRecord<TResource extends SdkResourceType & Pick<SdkResource, 'update'>>(
  resource: TResource,
  options?: SdkUseUpdateRecordOptions<SdkResourceRecord<TResource>>
): SdkUpdateRecordHandle<SdkResourceRecord<TResource>>;
export function useUpdateRecord(
  resource: Pick<SdkResource, 'update'>,
  options?: SdkUseUpdateRecordOptions
): SdkUpdateRecordHandle {
  const { run, ...state } = useRecordMutation(
    ({ recordId, payload }: UpdateVariables) =>
      resource.update(recordId, payload).then((response) => response.data),
    options
  );

  return {
    update: (recordId, payload) => run({ recordId, payload }),
    ...state
  };
}
